import { useCallback, useEffect } from 'react';
import { ApolloError } from '@apollo/client';

import { NotFoundError } from 'activate-errors';
import type { InspectionTemplate } from 'inspection-types';
import { useCurrentInspectionTemplate } from 'react-hooks';

import type { ReplicaLoadFailure } from '../model/operations';

import { readExceptionCode } from './errors';

export type UseTemplateQueryParams = {
  channelId: string | undefined;
  templateId: string | undefined;
};

export type UseTemplateQueryResult = {
  template: InspectionTemplate | undefined;
  loading: boolean;
  failure: ReplicaLoadFailure | undefined;
  load: () => Promise<void>;
  refresh: () => Promise<RefreshOutcome>;
};

export type RefreshOutcome =
  | { ok: true; template: InspectionTemplate }
  | { ok: false; failure: ReplicaLoadFailure; cause: unknown };

function isTemplateNotFound(error: Error): boolean {
  const { cause } = error;

  return (
    cause instanceof ApolloError &&
    cause.graphQLErrors.some(
      graphQLError =>
        readExceptionCode(graphQLError) === NotFoundError.errorName
    )
  );
}

function toLoadFailure(error: Error): ReplicaLoadFailure {
  return isTemplateNotFound(error) ? 'notFound' : 'error';
}

export function useTemplateQuery({
  channelId,
  templateId,
}: UseTemplateQueryParams): UseTemplateQueryResult {
  const { template, loading, error, fetchCurrentInspectionTemplate } =
    useCurrentInspectionTemplate();
  // A separate query instance keeps a failed refresh out of the page load failure
  const { fetchCurrentInspectionTemplate: fetchForRefresh } =
    useCurrentInspectionTemplate();

  const load = useCallback(async () => {
    if (!channelId || !templateId) {
      return;
    }

    await fetchCurrentInspectionTemplate({
      id: templateId,
      channelId,
      includeHidden: true,
    }).catch(() => undefined);
  }, [channelId, templateId, fetchCurrentInspectionTemplate]);

  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    try {
      const { template: refreshed } = await fetchForRefresh({
        id: templateId ?? '',
        channelId: channelId ?? '',
        includeHidden: true,
      });

      return { ok: true, template: refreshed };
    } catch (cause) {
      const failure = cause instanceof Error ? toLoadFailure(cause) : 'error';

      return { ok: false, failure, cause };
    }
  }, [channelId, templateId, fetchForRefresh]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    // The wrapped hook keeps the previous template until the new one resolves
    template: template?.id === templateId ? template : undefined,
    loading,
    failure: error ? toLoadFailure(error) : undefined,
    load,
    refresh,
  };
}
